import React from "react";
import { Link } from "react-router-dom";
import Icon from "@mdi/react";
import { mdiClockOutline, mdiCalendarOutline, mdiMapMarker, mdiArrowRight } from "@mdi/js";
import { slugify } from "../../../../utils/slugify";

function formatDate(dateString) {
  if (!dateString) return "";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
}

function stripHtml(html) {
  return String(html || "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
}

function estimateReadTime(html) {
  const words = stripHtml(html).split(" ").filter(Boolean).length;
  return `${Math.max(1, Math.ceil(words / 200))} min read`;
}

export default function FeaturedBlogCardGem({ blog, tagLabel = "BLOG", variant = "standard" }) {
  const blogSlug = blog?.slug || slugify(blog?.title || "");
  const blogUrl = blogSlug ? `/blog/${blogSlug}` : `/blog/${blog?.id}`;
  const image = blog?.images;
  const date = formatDate(blog?.created_at);
  const readTime = estimateReadTime(blog?.description);
  const excerpt = blog?.short_description || stripHtml(blog?.description).slice(0, 130);

  // === HERO VARIANT: Full-width magazine cover layout ===
  if (variant === "hero") {
    return (
      <Link
        to={blogUrl}
        className="feat-card"
        style={{
          width: "100%",
          display: "flex",
          flexDirection: "row",
          borderRadius: 18,
          overflow: "hidden",
          background: "white",
          border: "1px solid rgba(0,0,0,.05)",
          textDecoration: "none",
          color: "inherit",
          transition: "all .5s cubic-bezier(.22,1,.36,1)",
          position: "relative",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = "translateY(-4px)";
          e.currentTarget.style.boxShadow = "0 30px 70px -18px rgba(33,165,160,.3)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = "translateY(0)";
          e.currentTarget.style.boxShadow = "none";
        }}
      >
        {/* Image takes left 45% */}
        <div style={{
          width: "45%", minHeight: 300, overflow: "hidden", position: "relative", flexShrink: 0,
        }}>
          <img
            src={image}
            alt={blog?.title || "Blog"}
            style={{ width: "100%", height: "100%", objectFit: "cover", position: "absolute", inset: 0, transition: "transform .7s ease" }}
            onMouseEnter={(e) => { e.currentTarget.style.transform = "scale(1.08)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = "scale(1)"; }}
            onError={(e) => { e.currentTarget.style.background = "#EEF7F7"; e.currentTarget.style.display = "none"; }}
          />
          {/* Gradient overlay */}
          <div style={{
            position: "absolute", inset: 0,
            background: "linear-gradient(135deg, rgba(33,165,160,0.08), transparent 50%)",
            pointerEvents: "none",
          }} />
        </div>

        {/* Content takes right 55% */}
        <div style={{
          flex: 1, padding: "2rem 2.2rem",
          display: "flex", flexDirection: "column", justifyContent: "center",
        }}>
          <div style={{
            display: "flex", alignItems: "center", gap: 8, marginBottom: ".6rem", flexWrap: "wrap",
          }}>
            <span style={{
              display: "inline-flex", alignItems: "center", gap: 5,
              background: "linear-gradient(135deg, #EEF7F7, #e0f5f3)",
              color: "#128884", fontSize: ".72rem", fontWeight: 800,
              textTransform: "uppercase", letterSpacing: ".06em",
              padding: "4px 14px 4px 10px", borderRadius: 100,
              border: "1px solid rgba(33,165,160,.1)",
            }}>
              <Icon path={mdiMapMarker} size={0.5} />
              {tagLabel}
            </span>
            <span style={{
              display: "inline-flex", alignItems: "center", gap: 3,
              fontSize: ".75rem", color: "#74757B", fontWeight: 500,
            }}>
              <Icon path={mdiCalendarOutline} size={0.45} color="#74757B" />
              {date}
            </span>
            <span style={{
              display: "inline-flex", alignItems: "center", gap: 3,
              fontSize: ".75rem", color: "#74757B", fontWeight: 500,
            }}>
              <Icon path={mdiClockOutline} size={0.45} color="#74757B" />
              {readTime}
            </span>
          </div>

          <h3 style={{
            fontFamily: "'Inter', sans-serif", fontWeight: 800,
            fontSize: "clamp(1.3rem, 2.2vw, 1.7rem)", lineHeight: 1.3,
            color: "#0F0F0F", margin: "0 0 .6rem",
            display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden",
          }}>
            {blog?.title || "Untitled"}
          </h3>

          <p style={{
            fontSize: ".95rem", color: "#74757B", lineHeight: 1.7,
            margin: "0 0 1.2rem", flex: 1,
            display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden",
          }}>
            {excerpt}
          </p>

          <div style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            fontSize: ".9rem", fontWeight: 700, color: "#21A5A0",
            transition: "gap .3s ease",
          }}
            onMouseEnter={(e) => { e.currentTarget.style.gap = "12px"; e.currentTarget.style.color = "#E05A49"; }}
            onMouseLeave={(e) => { e.currentTarget.style.gap = "6px"; e.currentTarget.style.color = "#21A5A0"; }}
          >
            Read the story
            <Icon path={mdiArrowRight} size={0.65} />
          </div>
        </div>
      </Link>
    );
  }

  // === COMPACT VARIANT: Small side card ===
  if (variant === "compact") {
    return (
      <Link
        to={blogUrl}
        className="feat-card"
        style={{
          borderRadius: 16, overflow: "hidden", background: "white",
          border: "1px solid rgba(0,0,0,.05)", textDecoration: "none", color: "inherit",
          display: "flex", flexDirection: "column",
          transition: "all .4s cubic-bezier(.22,1,.36,1)",
          height: "100%",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = "translateY(-6px)";
          e.currentTarget.style.boxShadow = "0 20px 50px -14px rgba(33,165,160,.2)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = "translateY(0)";
          e.currentTarget.style.boxShadow = "none";
        }}
      >
        <div style={{
          height: 140, overflow: "hidden", position: "relative", flexShrink: 0,
        }}>
          <img
            src={image}
            alt={blog?.title || "Blog"}
            style={{ width: "100%", height: "100%", objectFit: "cover", transition: "transform .5s ease" }}
            onMouseEnter={(e) => { e.currentTarget.style.transform = "scale(1.08)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = "scale(1)"; }}
            onError={(e) => { e.currentTarget.style.background = "#EEF7F7"; e.currentTarget.style.display = "none"; }}
          />
          <span style={{
            position: "absolute", bottom: 8, left: 8,
            background: "rgba(255,255,255,0.9)", backdropFilter: "blur(4px)",
            color: "#128884", fontSize: ".65rem", fontWeight: 800,
            textTransform: "uppercase", letterSpacing: ".04em",
            padding: "2px 10px 2px 8px", borderRadius: 100,
            display: "inline-flex", alignItems: "center", gap: 4,
            border: "1px solid rgba(33,165,160,.1)",
          }}>
            <Icon path={mdiMapMarker} size={0.45} />
            {tagLabel}
          </span>
        </div>

        <div style={{
          padding: ".9rem 1rem 1.1rem", flex: 1,
          display: "flex", flexDirection: "column",
        }}>
          <div style={{
            display: "flex", alignItems: "center", gap: 4, marginBottom: ".3rem",
          }}>
            <Icon path={mdiCalendarOutline} size={0.4} color="#74757B" />
            <span style={{ fontSize: ".68rem", color: "#74757B", fontWeight: 500 }}>{date}</span>
          </div>
          <h4 style={{
            fontFamily: "'Inter', sans-serif", fontWeight: 800,
            fontSize: ".95rem", lineHeight: 1.35, color: "#0F0F0F",
            margin: "0 0 .3rem", flex: 1,
            display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden",
          }}>
            {blog?.title || "Untitled"}
          </h4>
          <div style={{
            display: "inline-flex", alignItems: "center", gap: 4,
            fontSize: ".75rem", fontWeight: 700, color: "#21A5A0",
            transition: "gap .3s ease",
          }}
            onMouseEnter={(e) => { e.currentTarget.style.gap = "10px"; e.currentTarget.style.color = "#E05A49"; }}
            onMouseLeave={(e) => { e.currentTarget.style.gap = "4px"; e.currentTarget.style.color = "#21A5A0"; }}
          >
            Explore
            <Icon path={mdiArrowRight} size={0.5} />
          </div>
        </div>
      </Link>
    );
  }

  // === STANDARD VARIANT ===
  return (
    <Link className="feat-card" to={blogUrl}>
      <div className="feat-card__img-wrap">
        <img
          src={image}
          alt={blog?.title || "Blog"}
          className="feat-card__img"
          onError={(e) => { e.currentTarget.style.background = "#EEF7F7"; e.currentTarget.style.display = "none"; }}
        />
        <div className="feat-card__img-overlay" />
        <span className="feat-card__badge">
          <Icon path={mdiMapMarker} size={0.5} />
          {tagLabel}
        </span>
        <div className="feat-card__read-floating">
          <Icon path={mdiClockOutline} size={0.5} color="#0B1F3A" />
          {readTime}
        </div>
        <div className="feat-card__number">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="#E05A49">
            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
          </svg>
        </div>
      </div>

      <div className="feat-card__body">
        <div className="feat-card__meta">
          <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
            <Icon path={mdiCalendarOutline} size={0.45} color="#21A5A0" />
            {date}
          </span>
        </div>
        <h3 className="feat-card__title">{blog?.title || "Untitled"}</h3>
        <p className="feat-card__excerpt">{excerpt}</p>
        <span className="feat-card__cta">
          Explore Guide
          <svg className="feat-card__cta-arrow" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M5 12h14M12 5l7 7-7 7" />
          </svg>
        </span>
      </div>
    </Link>
  );
}